import { Injectable } from "@angular/core";
import { createStore, withProps } from "@ngneat/elf";
import { StatisticsMetric } from "source/app/components/statistics/statistics.types";
import { localStorageStrategy, persistState } from "@ngneat/elf-persist-state";
import { debounceTime } from "rxjs";

const uiStore = createStore(
	{ name: "ui" },
	withProps<UiState>({
		seatsUnavailable: 0,
		cartsInUse: 0,
		unavailableUnitIds: [],
		unavailableSeatIdsByUnitId: {},
		allowChangingDispatchesWhenClosingLocation: true,
		allowChangingRidersWhenClosingLocation: true,
		defaultCartsHasBeenSet: false,
		lockedSettings: false,
		dashboardStatisticsMetric: StatisticsMetric.None,
		assetDispatchMetric: "occupiedSeats",
		assetDispatchInputMode: undefined,
		rideOpsUiV2OptIn: false,
		rideOpsUiV2FlagAvailable: false,
		assetHierarchyPrototypeFlagAvailable: false,
		dispatchUndoOptIn: false,
		rememberRiderCount: false,
		widgetOptions: {
			riders: {
				visible: true,
				mode: "grid",
				metric: "occupiedSeats",
				numberGridConfiguration: {
					amount: 100,
					direction: "ascending",
					amountConfigured: false,
				},
			},
			queueTime: {
				visible: true,
			},
			safariAiDispatch: {
				visible: true,
			},
			safariAiQueue: {
				visible: true,
			},
			bulletinWidget: {
				visible: true,
			},
			unitList: {
				visible: true,
			},
			dispatchStatistics: {
				visible: true,
			},
		},
	}),
);

@Injectable({ providedIn: "root" })
export class UIStore {
	constructor() {
		this.migrateAssetDispatchMetric();
		this.migrateWidgetOptions();
		persistState(uiStore, {
			storage: localStorageStrategy,
			source: () => uiStore.pipe(debounceTime(1000)),
		});
	}

	public store = uiStore;

	reset = () => {
		uiStore.reset();
	};

	private migrateAssetDispatchMetric = () => {
		const raw = localStorage.getItem("ui@store");
		if (raw == null) {
			return;
		}

		try {
			const parsed = JSON.parse(raw) as {
				assetDispatchMetric?: "emptySeats" | "occupiedSeats";
				assetDispatchMetricOverride?: "emptySeats" | "occupiedSeats";
			};
			if (parsed.assetDispatchMetric != null || parsed.assetDispatchMetricOverride == null) {
				return;
			}

			localStorage.setItem(
				"ui@store",
				JSON.stringify({
					...parsed,
					assetDispatchMetric: parsed.assetDispatchMetricOverride,
					assetDispatchMetricOverride: undefined,
				}),
			);
		} catch {
			// Ignore corrupt persisted state; elf will fall back to defaults.
		}
	};

	private migrateWidgetOptions = () => {
		const raw = localStorage.getItem("ui@store");
		if (raw == null) {
			return;
		}

		try {
			const parsed = JSON.parse(raw) as { widgetOptions?: UiState["widgetOptions"] };
			const widgetOptions = parsed.widgetOptions;
			if (widgetOptions == null) {
				return;
			}

			let changed = false;
			const nextWidgetOptions = { ...widgetOptions };

			if (nextWidgetOptions.dispatchStatistics == null) {
				nextWidgetOptions.dispatchStatistics = { visible: true };
				changed = true;
			}

			if (nextWidgetOptions.unitList == null) {
				nextWidgetOptions.unitList = { visible: true };
				changed = true;
			}

			if (!changed) {
				return;
			}

			localStorage.setItem(
				"ui@store",
				JSON.stringify({
					...parsed,
					widgetOptions: nextWidgetOptions,
				}),
			);
		} catch {
			// Ignore corrupt persisted state; elf will fall back to defaults.
		}
	};
}

export interface UiState {
	seatsUnavailable: number;
	cartsInUse: number;
	/** Asset-mode: operational unit asset ids marked unavailable on this device. */
	unavailableUnitIds: string[];
	/** Asset-mode: seat asset ids marked unavailable, keyed by unit asset id. */
	unavailableSeatIdsByUnitId: Record<string, string[]>;
	allowChangingDispatchesWhenClosingLocation: boolean;
	allowChangingRidersWhenClosingLocation: boolean;
	defaultCartsHasBeenSet: boolean; // Should probably be called cartsInUseHasBeenManuallySet, but for backwards compatibility it is not renamed
	lockedSettings: boolean;
	dashboardStatisticsMetric: StatisticsMetric;
	// Per-device metric for the asset-dispatch widget: "occupiedSeats" (default) or "emptySeats".
	// Distinct from deviceSettings.dispatchMetric used by the legacy riders widget. Persisted locally.
	assetDispatchMetric: "emptySeats" | "occupiedSeats";
	// Operator-facing LOCAL choice of asset-dispatch INPUT widget. `undefined` is treated as
	// "selector" (the counter), preserving the previous asset-widget behaviour; "grid" switches to
	// the numpad. This is independent of the legacy riders-widget device-level `mode` and is
	// persisted with the rest of the UI store so it survives reloads.
	assetDispatchInputMode?: "grid" | "selector";
	/** Local device opt-in for RideOps v2 UI. Default off — users stay on v1 until enabled in Settings. */
	rideOpsUiV2OptIn: boolean;
	/**
	 * Last known CORE `RideOpsUiV2` availability from a signed-in flag load.
	 * Used when signed out (flags are cleared) so v2 can stay active on `/operators`.
	 */
	rideOpsUiV2FlagAvailable: boolean;
	/**
	 * Last known CORE `rideops__assetHierarchyPrototype` from a signed-in flag load.
	 * Used when flags are cleared (offline refresh failure) so asset dispatch mode does not drop.
	 */
	assetHierarchyPrototypeFlagAvailable: boolean;
	dispatchUndoOptIn: boolean;
	/** When true, asset-dispatch keeps a shared last occupied-seat count across units. Default off. */
	rememberRiderCount: boolean;
	widgetOptions: {
		riders: RidersWidgetOptions;
		queueTime: WidgetOptions;
		safariAiDispatch?: WidgetOptions;
		safariAiQueue?: WidgetOptions;
		bulletinWidget?: WidgetOptions;
		unitList?: WidgetOptions;
		dispatchStatistics?: WidgetOptions;
	};
}

export interface WidgetOptions {
	visible: boolean;
}

export interface RidersWidgetOptions extends WidgetOptions {
	mode?: "selector" | "grid";
	metric?: "emptySeats" | "occupiedSeats";
	numberGridConfiguration: NumberGridConfiguration;
}

export interface NumberGridConfiguration {
	amount: number;
	amountConfigured: boolean;
	direction: "ascending" | "descending";
}

export type WidgetKey = keyof UiState["widgetOptions"];
